import { useCallback, useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { StatusBadge } from "@/components/Layout";
import { toast } from "sonner";
import { Filter } from "lucide-react";

const FILTERS = [
  { v: "", label: "All" },
  { v: "triggered", label: "Triggered" },
  { v: "sent", label: "Sent" },
  { v: "failed", label: "Failed" },
  { v: "blocked_by_meta", label: "Meta Blocked" },
  { v: "queued", label: "Prepared" },
  { v: "prepared", label: "Prepared" },
  { v: "blocked_quota", label: "Quota Hit" },
];

export default function Triggers() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("");

  const load = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    try {
      const { data } = await api.get("/triggers", { params: { status: filter || undefined, limit: 200 } });
      setItems(data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  }, [filter]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    const id = window.setInterval(() => load(true), 5000);
    return () => window.clearInterval(id);
  }, [load]);

  return (
    <div className="space-y-6" data-testid="triggers-page">
      <header>
        <span className="chip bg-peach">History</span>
        <h1 className="text-4xl md:text-5xl font-extrabold mt-3">Trigger History</h1>
        <p className="text-muted-foreground font-medium mt-1">Every comment matched and DM sent.</p>
      </header>

      <div className="flex flex-wrap items-center gap-2">
        <Filter className="w-4 h-4" strokeWidth={2.5} />
        {FILTERS.map((f) => (
          <button
            key={f.v}
            onClick={() => setFilter(f.v)}
            data-testid={`filter-${f.v || "all"}`}
            className={`px-3 py-1.5 border-2 border-foreground rounded-md text-sm font-bold ${filter === f.v ? "bg-primary shadow-brutal-sm" : "bg-white"}`}
          >
            {f.label}
          </button>
        ))}
      </div>

      <div className="brutal bg-white overflow-hidden">
        {loading ? (
          <div className="p-10 text-center font-bold">Loading…</div>
        ) : items.length === 0 ? (
          <div className="p-10 text-center font-bold">No triggers yet for this filter.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-muted border-b-2 border-foreground">
                <tr>
                  <th className="text-left p-3 font-bold uppercase text-xs tracking-wider">Commenter</th>
                  <th className="text-left p-3 font-bold uppercase text-xs tracking-wider">Comment</th>
                  <th className="text-left p-3 font-bold uppercase text-xs tracking-wider">Keyword</th>
                  <th className="text-left p-3 font-bold uppercase text-xs tracking-wider">Status</th>
                  <th className="text-left p-3 font-bold uppercase text-xs tracking-wider">Time</th>
                </tr>
              </thead>
              <tbody>
                {items.map((t) => (
                  <tr key={t.id} className="border-b border-foreground/20" data-testid={`tr-${t.id}`}>
                    <td className="p-3 font-bold">@{t.commenter_username}</td>
                    <td className="p-3 font-medium max-w-xs truncate">"{t.comment_text}"</td>
                    <td className="p-3"><span className="chip bg-butter text-[10px]">{t.keyword_matched}</span></td>
                    <td className="p-3"><StatusBadge status={t.dm_status} /></td>
                    <td className="p-3 font-semibold text-xs text-muted-foreground">{new Date(t.created_at).toLocaleString()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
